import { createContext, useCallback, useContext, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { cn } from '../../utils/cn';
import { Icon, type IconName } from '../../icons/Icon';
import { Button } from '../Button/Button';
import { Input } from '../Input/Input';
import { Modal } from '../Modal/Modal';
import { Textarea } from '../Textarea/Textarea';

export type ConfirmTone = 'neutral' | 'danger' | 'warning';

export interface ConfirmOptions {
  title: ReactNode;
  message?: ReactNode;
  tone?: ConfirmTone;
  confirmLabel?: string;
  cancelLabel?: string;
  /** The operator must type this exact text before the confirm button enables. */
  requireText?: string;
  /** Label of the typed-confirmation field; `{text}` is replaced by `requireText`. */
  requireTextLabel?: string;
  /**
   * D-016: a confirmation that records a reason (an override, a write-off). Read the text with
   * `useConfirmWithReason`; with `required`, the confirm button waits for it.
   */
  reason?: { label: string; placeholder?: string; hint?: string; required?: boolean };
  /**
   * L-05: a third way out beside cancel and confirm ("Keep as draft"): runs `onSelect`, then the confirm resolves `true`
   * — the caller proceeds as if confirmed, with the work kept.
   */
  extraAction?: { label: string; onSelect: () => void | Promise<void> };
}

/** Resolves the typed reason when confirmed (trimmed; `''` when optional and left empty), `null` when cancelled. */
export type ConfirmWithReasonFn = (options: ConfirmOptions & { reason: NonNullable<ConfirmOptions['reason']> }) => Promise<string | null>;

export type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);
const ReasonContext = createContext<ConfirmWithReasonFn | null>(null);

interface PendingConfirm {
  key: number;
  options: ConfirmOptions;
  resolve: (value: boolean) => void;
}

const toneIcon: Record<ConfirmTone, IconName | null> = { neutral: null, danger: 'alert-circle', warning: 'alert' };
const toneTile: Record<ConfirmTone, string> = {
  neutral: '',
  danger: 'bg-danger-subtle text-danger-fg',
  warning: 'bg-warning-subtle text-warning-fg',
};

export interface ConfirmProviderProps {
  children: ReactNode;
  /** Defaults for every confirmation this provider shows. */
  defaultConfirmLabel?: string;
  defaultCancelLabel?: string;
  defaultRequireTextLabel?: string;
}

/**
 * Imperative confirmation service. Mounts one `Modal` and hands out `confirm(options)`, which resolves `true`
 * when the operator confirms and `false` when they cancel, press Escape or click the backdrop. A second call
 * while one is open resolves the first as cancelled and replaces it.
 */
export function ConfirmProvider({
  children,
  defaultConfirmLabel = 'Confirm',
  defaultCancelLabel = 'Cancel',
  defaultRequireTextLabel = 'Type {text} to confirm',
}: ConfirmProviderProps) {
  const [pending, setPending] = useState<PendingConfirm | null>(null);
  const [typed, setTyped] = useState('');
  const [reasonText, setReasonText] = useState('');
  const reasonRef = useRef('');
  reasonRef.current = reasonText;
  const counter = useRef(0);
  const pendingRef = useRef<PendingConfirm | null>(null);
  pendingRef.current = pending;
  const confirmRef = useRef<HTMLButtonElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const id = useId();

  const confirm = useCallback<ConfirmFn>((options) => {
    pendingRef.current?.resolve(false);
    counter.current += 1;
    const key = counter.current;
    return new Promise<boolean>((resolve) => {
      setTyped('');
      setReasonText('');
      setPending({ key, options, resolve });
    });
  }, []);
  const confirmWithReason = useCallback<ConfirmWithReasonFn>((options) => confirm(options).then((ok) => (ok ? reasonRef.current.trim() : null)), [confirm]);

  const settle = useCallback((value: boolean) => {
    const current = pendingRef.current;
    if (!current) return;
    setPending(null);
    current.resolve(value);
  }, []);
  const cancel = useCallback(() => settle(false), [settle]);
  const accept = useCallback(() => settle(true), [settle]);

  // Resolve anything still open if the provider unmounts, so awaiting callers are never left hanging.
  useEffect(() => () => pendingRef.current?.resolve(false), []);

  const options = pending?.options;
  const tone = options?.tone ?? 'neutral';
  const requireText = options?.requireText;
  const reason = options?.reason;
  const matches = (!requireText || typed === requireText) && (!reason?.required || reasonText.trim().length > 0);
  const icon = toneIcon[tone];
  const requireLabel = (options?.requireTextLabel ?? defaultRequireTextLabel).replace('{text}', requireText ?? '');
  const initialFocus = requireText ? inputRef : tone === 'danger' ? undefined : confirmRef;

  return (
    <ConfirmContext.Provider value={confirm}>
      <ReasonContext.Provider value={confirmWithReason}>
      {children}
      <Modal
        key={pending?.key ?? 'idle'}
        open={pending != null}
        onClose={cancel}
        size="sm"
        title={options?.title ?? ''}
        initialFocusRef={initialFocus}
        data-confirm-tone={tone}
        footer={
          <>
            <Button variant="ghost" onClick={cancel}>
              {options?.cancelLabel ?? defaultCancelLabel}
            </Button>
            {options?.extraAction ? (
              <Button
                variant="secondary"
                onClick={() => {
                  const run = options.extraAction!.onSelect;
                  void Promise.resolve(run()).then(accept, accept);
                }}
              >
                {options.extraAction.label}
              </Button>
            ) : null}
            <Button ref={confirmRef} variant={tone === 'danger' ? 'danger' : 'primary'} onClick={accept} disabled={!matches}>
              {options?.confirmLabel ?? defaultConfirmLabel}
            </Button>
          </>
        }
      >
        <div className="flex gap-3">
          {icon ? (
            <span aria-hidden="true" className={cn('inline-flex size-8 shrink-0 items-center justify-center rounded-lg', toneTile[tone])}>
              <Icon name={icon} size={16} />
            </span>
          ) : null}
          <div className="min-w-0 flex-1">
            {options?.message != null ? <div className="type-body text-fg">{options.message}</div> : null}
            {reason ? (
              <div className="mt-3">
                <label htmlFor={`${id}-reason`} className="mb-1 block type-label text-fg">
                  {reason.label}
                </label>
                <Textarea id={`${id}-reason`} value={reasonText} onValueChange={setReasonText} minRows={2} placeholder={reason.placeholder} autoFocus />
                {reason.hint ? <p className="m-0 mt-1 type-caption text-fg-muted">{reason.hint}</p> : null}
              </div>
            ) : null}
            {requireText ? (
              <div className="mt-3">
                <label htmlFor={`${id}-require`} className="mb-1 block type-label text-fg">
                  {requireLabel}
                </label>
                <Input
                  ref={inputRef}
                  id={`${id}-require`}
                  value={typed}
                  onValueChange={setTyped}
                  mono
                  autoComplete="off"
                  spellCheck={false}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && matches) accept();
                  }}
                />
              </div>
            ) : null}
          </div>
        </div>
      </Modal>
      </ReasonContext.Provider>
    </ConfirmContext.Provider>
  );
}

/** `confirm(options)` with a reason field; resolves the reason, or `null` when cancelled. */
export function useConfirmWithReason(): ConfirmWithReasonFn {
  const ctx = useContext(ReasonContext);
  if (!ctx) throw new Error('useConfirmWithReason must be used within <ConfirmProvider>');
  return ctx;
}

/** `confirm(options)` from the nearest `ConfirmProvider`; resolves `true` when the operator confirms. */
export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within <ConfirmProvider>');
  return ctx;
}
